/**
 * Connections API Service
 *
 * Provides API methods for managing user connections ("friends list") including:
 * - Fetching accepted connections
 * - Sending, accepting, declining connection requests
 * - Token-based operations (for email links)
 * - User search for connections
 * - Blocking/unblocking users
 */

import { api } from './client';
import type { ApiResponse } from '@/types';
import type {
  UserConnection,
  PendingConnectionRequest,
  SentConnectionRequest,
  UserSearchResult,
  MutualConnection,
  ConnectionRequestDetails,
  SendConnectionRequestResponse,
  AcceptConnectionResponse,
  DeclineConnectionResponse,
  MutualConnectionsResponse,
  ConnectionAcceptResult,
  UserPublicProfile,
  ConnectionSuggestion,
  ConnectionSuggestionsResponse,
  ConnectionActivity,
  BlockedUsersResponse,
} from '@/types/connections';

const CONNECTIONS_BASE_URL = '/connections';
const USERS_BASE_URL = '/users';

export const connectionsApi = {
  // ============================================================================
  // Authenticated Connection Endpoints
  // ============================================================================

  /**
   * Get user's accepted connections
   */
  async getConnections(): Promise<ApiResponse<UserConnection[]>> {
    console.log('[ConnectionsApi] getConnections - fetching accepted connections');
    const response = await api.get<UserConnection[]>(CONNECTIONS_BASE_URL);
    console.log('[ConnectionsApi] getConnections - found:', response.data?.length ?? 0);
    return response;
  },

  /**
   * Get pending connection requests received
   */
  async getPendingReceived(): Promise<ApiResponse<PendingConnectionRequest[]>> {
    console.log('[ConnectionsApi] getPendingReceived - fetching pending received requests');
    const response = await api.get<PendingConnectionRequest[]>(
      `${CONNECTIONS_BASE_URL}/pending/received`
    );
    console.log('[ConnectionsApi] getPendingReceived - found:', response.data?.length ?? 0);
    return response;
  },

  /**
   * Get pending connection requests sent
   */
  async getPendingSent(): Promise<ApiResponse<SentConnectionRequest[]>> {
    console.log('[ConnectionsApi] getPendingSent - fetching pending sent requests');
    const response = await api.get<SentConnectionRequest[]>(
      `${CONNECTIONS_BASE_URL}/pending/sent`
    );
    console.log('[ConnectionsApi] getPendingSent - found:', response.data?.length ?? 0);
    return response;
  },

  /**
   * Get mutual connections with another user (supports pagination)
   */
  async getMutualConnections(
    userId: string,
    options: { page?: number; limit?: number; countOnly?: boolean } = {}
  ): Promise<ApiResponse<MutualConnectionsResponse>> {
    const { page = 1, limit = 20, countOnly = false } = options;
    console.log('[MutualConnections] getMutualConnections - userId:', userId, 'page:', page, 'countOnly:', countOnly);
    const response = await api.get<MutualConnectionsResponse>(
      `${CONNECTIONS_BASE_URL}/mutual/${userId}`,
      { params: { page, limit, countOnly } }
    );
    console.log('[MutualConnections] getMutualConnections - found:', response.data?.count ?? 0);
    return response;
  },

  /**
   * Get mutual connections count only (optimized)
   */
  async getMutualConnectionsCount(userId: string): Promise<ApiResponse<{ count: number }>> {
    console.log('[MutualConnections] getMutualConnectionsCount - userId:', userId);
    const response = await api.get<{ count: number }>(
      `${CONNECTIONS_BASE_URL}/mutual/${userId}`,
      { params: { countOnly: true } }
    );
    console.log('[MutualConnections] getMutualConnectionsCount - count:', response.data?.count ?? 0);
    return response;
  },

  // ============================================================================
  // Connection Suggestions ("People You May Know")
  // ============================================================================

  /**
   * Get connection suggestions for the current user
   */
  async getSuggestions(options: { limit?: number } = {}): Promise<ApiResponse<ConnectionSuggestionsResponse>> {
    const { limit = 10 } = options;
    console.log('[ConnectionSuggestions] getSuggestions - limit:', limit);
    const response = await api.get<ConnectionSuggestionsResponse>(
      `${CONNECTIONS_BASE_URL}/suggestions`,
      { params: { limit } }
    );
    console.log('[ConnectionSuggestions] getSuggestions - found:', response.data?.suggestions?.length ?? 0);
    return response;
  },

  /**
   * Dismiss a connection suggestion
   */
  async dismissSuggestion(userId: string): Promise<ApiResponse<void>> {
    console.log('[ConnectionSuggestions] dismissSuggestion - userId:', userId);
    const response = await api.post<void>(
      `${CONNECTIONS_BASE_URL}/suggestions/${userId}/dismiss`
    );
    console.log('[ConnectionSuggestions] dismissSuggestion - success');
    return response;
  },

  // ============================================================================
  // Connection Activity
  // ============================================================================

  /**
   * Get connection activity (notifications)
   */
  async getActivity(options: { limit?: number; unreadOnly?: boolean } = {}): Promise<ApiResponse<ConnectionActivity[]>> {
    const { limit = 20, unreadOnly = false } = options;
    console.log('[ConnectionActivity] getActivity - limit:', limit, 'unreadOnly:', unreadOnly);
    const response = await api.get<ConnectionActivity[]>(
      `${CONNECTIONS_BASE_URL}/activity`,
      { params: { limit, unreadOnly } }
    );
    console.log('[ConnectionActivity] getActivity - found:', response.data?.length ?? 0);
    return response;
  },

  /**
   * Mark connection activity as read
   */
  async markActivityRead(activityIds: string[]): Promise<ApiResponse<void>> {
    console.log('[ConnectionActivity] markActivityRead - ids:', activityIds.length);
    const response = await api.post<void>(
      `${CONNECTIONS_BASE_URL}/activity/read`,
      { activityIds }
    );
    console.log('[ConnectionActivity] markActivityRead - success');
    return response;
  },

  /**
   * Send a connection request to another user
   */
  async sendRequest(
    userId: string,
    message?: string
  ): Promise<ApiResponse<SendConnectionRequestResponse>> {
    console.log('[ConnectionsApi] sendRequest - userId:', userId, 'message:', !!message);
    const response = await api.post<SendConnectionRequestResponse>(
      `${CONNECTIONS_BASE_URL}/request`,
      { userId, message }
    );
    console.log('[ConnectionsApi] sendRequest - success, connectionId:', response.data?.connectionId);
    return response;
  },

  /**
   * Accept a connection request by ID (authenticated user)
   */
  async acceptRequest(connectionId: string): Promise<ApiResponse<AcceptConnectionResponse>> {
    console.log('[ConnectionsApi] acceptRequest - connectionId:', connectionId);
    const response = await api.post<AcceptConnectionResponse>(
      `${CONNECTIONS_BASE_URL}/${connectionId}/accept`
    );
    console.log('[ConnectionsApi] acceptRequest - success');
    return response;
  },

  /**
   * Decline a connection request by ID (authenticated user)
   */
  async declineRequest(connectionId: string): Promise<ApiResponse<DeclineConnectionResponse>> {
    console.log('[ConnectionsApi] declineRequest - connectionId:', connectionId);
    const response = await api.post<DeclineConnectionResponse>(
      `${CONNECTIONS_BASE_URL}/${connectionId}/decline`
    );
    console.log('[ConnectionsApi] declineRequest - success');
    return response;
  },

  /**
   * Cancel a sent connection request
   */
  async cancelRequest(connectionId: string): Promise<ApiResponse<void>> {
    console.log('[ConnectionsApi] cancelRequest - connectionId:', connectionId);
    const response = await api.post<void>(
      `${CONNECTIONS_BASE_URL}/${connectionId}/cancel`
    );
    console.log('[ConnectionsApi] cancelRequest - success');
    return response;
  },

  /**
   * Remove an accepted connection
   */
  async removeConnection(connectionId: string): Promise<ApiResponse<void>> {
    console.log('[ConnectionsApi] removeConnection - connectionId:', connectionId);
    const response = await api.delete<void>(
      `${CONNECTIONS_BASE_URL}/${connectionId}`
    );
    console.log('[ConnectionsApi] removeConnection - success');
    return response;
  },

  /**
   * Block a user
   */
  async blockUser(userId: string): Promise<ApiResponse<void>> {
    console.log('[ConnectionsApi] blockUser - userId:', userId);
    const response = await api.post<void>(
      `${CONNECTIONS_BASE_URL}/block/${userId}`
    );
    console.log('[ConnectionsApi] blockUser - success');
    return response;
  },

  /**
   * Unblock a user
   */
  async unblockUser(userId: string): Promise<ApiResponse<void>> {
    console.log('[ConnectionsApi] unblockUser - userId:', userId);
    const response = await api.post<void>(
      `${CONNECTIONS_BASE_URL}/unblock/${userId}`
    );
    console.log('[ConnectionsApi] unblockUser - success');
    return response;
  },

  /**
   * Get list of blocked users
   */
  async getBlockedUsers(): Promise<ApiResponse<BlockedUsersResponse>> {
    console.log('[ConnectionsApi] getBlockedUsers');
    const response = await api.get<BlockedUsersResponse>(
      `${CONNECTIONS_BASE_URL}/blocked`
    );
    console.log('[ConnectionsApi] getBlockedUsers - found:', response.data?.total ?? 0);
    return response;
  },

  // ============================================================================
  // Token-Based Endpoints (for email links)
  // ============================================================================

  /**
   * Get connection request details by token (for preview before accepting)
   */
  async getRequestByToken(token: string): Promise<ApiResponse<ConnectionRequestDetails>> {
    console.log('[ConnectionsApi] getRequestByToken - token prefix:', token.substring(0, 8) + '...');
    const response = await api.get<ConnectionRequestDetails>(
      `${CONNECTIONS_BASE_URL}/token/${token}`
    );
    console.log('[ConnectionsApi] getRequestByToken - status:', response.data?.status);
    return response;
  },

  /**
   * Accept connection request via token (from email link)
   */
  async acceptByToken(token: string): Promise<ApiResponse<ConnectionAcceptResult>> {
    console.log('[ConnectionsApi] acceptByToken - token prefix:', token.substring(0, 8) + '...');
    const response = await api.post<ConnectionAcceptResult>(
      `${CONNECTIONS_BASE_URL}/token/${token}/accept`
    );
    console.log('[ConnectionsApi] acceptByToken - success');
    return response;
  },

  /**
   * Decline connection request via token (from email link)
   */
  async declineByToken(token: string): Promise<ApiResponse<DeclineConnectionResponse>> {
    console.log('[ConnectionsApi] declineByToken - token prefix:', token.substring(0, 8) + '...');
    const response = await api.post<DeclineConnectionResponse>(
      `${CONNECTIONS_BASE_URL}/token/${token}/decline`
    );
    console.log('[ConnectionsApi] declineByToken - success');
    return response;
  },

  // ============================================================================
  // User Search Endpoints
  // ============================================================================

  /**
   * Search users for connections
   */
  async searchUsers(query: string, limit = 20): Promise<ApiResponse<UserSearchResult[]>> {
    console.log('[ConnectionsApi] searchUsers - query:', query, 'limit:', limit);
    const response = await api.get<UserSearchResult[]>(
      `${USERS_BASE_URL}/search/connections`,
      { params: { q: query, limit } }
    );
    console.log('[ConnectionsApi] searchUsers - found:', response.data?.length ?? 0);
    return response;
  },

  // ============================================================================
  // User Profile Endpoints
  // ============================================================================

  /**
   * Get a user's public profile with connection status
   */
  async getUserProfile(userId: string): Promise<ApiResponse<UserPublicProfile>> {
    console.log('[ConnectionsApi] getUserProfile - userId:', userId);
    const response = await api.get<UserPublicProfile>(
      `${USERS_BASE_URL}/${userId}/profile`
    );
    console.log('[ConnectionsApi] getUserProfile - status:', response.data?.connectionStatus);
    return response;
  },

  /**
   * Get a user's public profile by username
   */
  async getUserProfileByUsername(username: string): Promise<ApiResponse<UserPublicProfile>> {
    console.log('[ConnectionsApi] getUserProfileByUsername - username:', username);
    const response = await api.get<UserPublicProfile>(
      `${USERS_BASE_URL}/username/${username}/profile`
    );
    console.log('[ConnectionsApi] getUserProfileByUsername - status:', response.data?.connectionStatus);
    return response;
  },
};
